import React from 'react';

import { CustomOperatorValue } from '@aline/common';
import type {
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterOperator,
} from '@mui/x-data-grid-premium';
import type { WorkflowTypeFilterOption } from 'src/components/workflow/workflow-list/workflow-type';

import { WorkflowTypeAutocompleteFilter } from './WorkflowTypeAutocompleteFilter';

interface WorkflowTypeFilterItem extends GridFilterItem {
  operator: CustomOperatorValue.WorkflowType | CustomOperatorValue.WorkflowTypeNotAnyOf;
  value?: WorkflowTypeFilterOption[];
}

function getIsWorkflowTypeFilterItem(item: GridFilterItem): item is WorkflowTypeFilterItem {
  return (
    item.operator === CustomOperatorValue.WorkflowType ||
    item.operator === CustomOperatorValue.WorkflowTypeNotAnyOf
  );
}

function getMatchesWorkflowType(
  filterValue: WorkflowTypeFilterOption[],
  sourceWorkflowId: string | null
): boolean {
  let filterCustom = false;
  const isCustom = !sourceWorkflowId;
  const filterTemplateIds = new Set<string>();
  filterValue.forEach(option => {
    if (option.__typename === 'CustomWorkflowType') {
      filterCustom = true;
      return;
    }
    filterTemplateIds.add(option.id);
  });

  return (
    (filterCustom && isCustom) || (!!sourceWorkflowId && filterTemplateIds.has(sourceWorkflowId))
  );
}

export const workflowTypeAnyOfFilterOperator: GridFilterOperator = {
  InputComponent: (props: GridFilterInputValueProps) => (
    <WorkflowTypeAutocompleteFilter {...props} />
  ),
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !filterItem.value ||
      !filterItem.operator ||
      !getIsWorkflowTypeFilterItem(filterItem)
    )
      return null;

    return cellParams => {
      if (!Array.isArray(filterItem.value) || !filterItem.value.length) return true;
      const sourceWorkflowId =
        typeof cellParams.value === 'string' || cellParams.value === null ? cellParams.value : null;
      return getMatchesWorkflowType(filterItem.value, sourceWorkflowId);
    };
  },
  label: 'is any of',
  value: CustomOperatorValue.WorkflowType,
};

export const workflowTypeNotAnyOfFilterOperator: GridFilterOperator = {
  InputComponent: (props: GridFilterInputValueProps) => (
    <WorkflowTypeAutocompleteFilter {...props} />
  ),
  getApplyFilterFn: filterItem => {
    if (!filterItem.field || !filterItem.operator || !getIsWorkflowTypeFilterItem(filterItem))
      return null;

    return cellParams => {
      if (!Array.isArray(filterItem.value) || !filterItem.value.length) return true;
      const sourceWorkflowId =
        typeof cellParams.value === 'string' || cellParams.value === null ? cellParams.value : null;
      return !getMatchesWorkflowType(filterItem.value, sourceWorkflowId);
    };
  },
  label: 'is none of',
  value: CustomOperatorValue.WorkflowTypeNotAnyOf,
};

export const allWorkflowTypeFilters = [
  workflowTypeAnyOfFilterOperator,
  workflowTypeNotAnyOfFilterOperator,
];
